import CuesheetDataHelper from './CuesheetDataHelper';
import CuesheetColumnActions from 'flux/actions/cuesheet/CuesheetColumnActions';
import CuesheetCellActions from 'flux/actions/cuesheet/CuesheetCellActions';
import CuesheetRowActions from 'flux/actions/cuesheet/CuesheetRowActions';
import ToastActions from 'flux/actions/ToastActions';
import AlertActions from 'flux/actions/AlertActions';
import { Map, List } from 'immutable';
import readOnlyHelper from 'flux/helpers/readOnlyHelper';
import {
  EditableCellTypes,
  ColumnWidthTokens,
} from 'flux/stores/cuesheet/CuesheetConstants';
import { DisplayTypes } from 'shared/constants/FlowConstants';
import MessageConstants from 'shared/constants/Messages';
import UIRegions from 'shared/constants/UIRegions';

/**
 * CuesheetStore Column Mixin
 *
 */
export default {
  onSaveFeaturedColumn(featuredColumn) {
    let next = this.getState();
    const selectedCell = this.getSelectedCell(),
      cellSpec = selectedCell ? selectedCell.cellSpec : {};

    if (!featuredColumn && cellSpec.get('type') == EditableCellTypes.Data) {
      featuredColumn = cellSpec.get('value');
    }

    next = next.setIn(['userSettings', 'featuredColumn'], featuredColumn);

    CuesheetColumnActions.updateUserSettingsPersistent(
      next.get('teamId'),
      next.get('eventId'),
      next.get('showId'),
      next.get('itemSetListId'),
      next.get('userId'),
      { featuredColumn },
    );

    next = this.touchAllRows(next);

    this.setStateAndTrigger(next);
  },

  onUpdateUserSettingsPersistentCompleted() {
    ToastActions.saved();
  },

  onHideItemDescriptions() {
    this.setItemDescriptionVisibilityTransient(false);

    let state = this.getState();

    CuesheetColumnActions.hideItemDescriptionsPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
    );
  },

  onShowItemDescriptions() {
    this.setItemDescriptionVisibilityTransient(true);

    let state = this.getState();

    CuesheetColumnActions.showItemDescriptionsPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
    );
  },

  onHideProofColumn() {
    let state = this.getState();

    CuesheetColumnActions.hideProofColumnPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
    );

    this.setInStateAndTrigger(['show', 'displayProofOfPerformance'], false);
  },

  onEnableProofTracking() {
    let state = this.getState();

    CuesheetColumnActions.enableProofTrackingPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
    );

    this.setInStateAndTrigger(['show', 'proofOfPerformanceTracking'], true);
  },

  onDisableProofTracking() {
    let state = this.getState();

    CuesheetColumnActions.disableProofTrackingPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
    );

    this.setInStateAndTrigger(['show', 'proofOfPerformanceTracking'], false);
  },

  onClearProofOfPerformance() {
    const state = this.getState(),
      mutableState = state.asMutable();

    let rowIds = [];

    state.get('rows').forEach((rowSpec, rowIndex) => {
      let rowPath = rowSpec.get('path');
      let proofPath = ['rowMap'].concat(rowPath.concat('proof'));
      let proof = this.getInState(proofPath);
      if (proof) {
        let rowId = rowPath[2];
        rowIds.push(rowId);
        mutableState.setIn(['rows', rowIndex, 'touch'], new Date().getTime());
        mutableState.deleteIn(proofPath);
      }
    });

    this.setStateAndTrigger(mutableState.asImmutable());

    CuesheetColumnActions.clearProofOfPerformancePersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
    );

    CuesheetRowActions.bustRowHeightCache(rowIds);
  },

  setItemDescriptionVisibilityTransient(displayDescriptions) {
    this.setInStateAndTrigger(
      ['show', 'displayItemDescriptions'],
      displayDescriptions,
    );

    CuesheetRowActions.bustRowHeightCache();
  },

  onUpdateItemDescriptionColumnWidth(width) {
    this.updateItemColumnSettingsTransient({ width }, 'itemDescriptionColumn');

    let state = this.getState();

    CuesheetColumnActions.updateItemDescriptionColumnWidthPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      width,
    );
  },

  onUpdateItemColumnWidth(width) {
    this.updateItemColumnSettingsTransient({ width });

    let state = this.getState();

    CuesheetColumnActions.updateItemColumnWidthPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      width,
    );
  },

  onUpdateItemTitleColumnWidth(width) {
    this.updateItemColumnSettingsTransient({ width }, 'itemTitleColumn');

    let state = this.getState();

    CuesheetColumnActions.updateItemTitleColumnWidthPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      width,
    );
  },

  onUpdateNoteColumnWidth(width) {
    let state = this.setInState(['userSettings', 'noteColumn', 'width'], width);

    CuesheetColumnActions.updateNoteColumnWidthPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      state.get('userId'),
      width,
    );

    CuesheetRowActions.bustRowHeightCache();

    this.setStateAndTrigger(state);
  },

  onHideNoteColumn() {
    this.setInState(['userSettings', 'noteColumn', 'hidden'], true);

    let state = this.rebuildSelectableCellList();

    CuesheetColumnActions.hideNoteColumnPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      state.get('userId'),
    );

    CuesheetRowActions.bustRowHeightCache();

    this.setStateAndTrigger(state);
  },

  onShowNoteColumn() {
    this.setInState(['userSettings', 'noteColumn', 'hidden'], false);

    let state = this.rebuildSelectableCellList();

    CuesheetColumnActions.showNoteColumnPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      state.get('userId'),
    );

    CuesheetRowActions.bustRowHeightCache();

    this.setStateAndTrigger(state);
  },

  /**
   * Change a column width
   */
  onUpdateColumnWidth(columnId, width) {
    this._updateColumnWidthTransient(columnId, width);
    this._updateColumnWidthPersistent(columnId, width);
  },

  onUpdateColumnWidthGrow() {
    this._changeColumnWidth('grow');
  },

  onUpdateColumnWidthShrink() {
    this._changeColumnWidth('shrink');
  },

  _changeColumnWidth(type) {
    let displayType = this.getState().get('show').get('displayType');

    let selectedCell = this.getSelectedCell();
    let selectedColumn = this.getSelectedColumn();

    if (!selectedCell && !selectedColumn) return;

    if (!selectedCell && selectedColumn) {
      this._changeDataColumnWidth(type);
      return;
    }

    let { cellSpec } = selectedCell,
      cellType = cellSpec.get('type');

    if (cellType === EditableCellTypes.ItemDescription) {
      if (displayType !== DisplayTypes.show) {
        this._changeUtilityColumnWidth(
          type,
          'itemDescriptionColumn',
          CuesheetColumnActions.updateItemDescriptionColumnWidth,
        );
      } else {
        this._changeUtilityColumnWidth(
          type,
          'itemColumn',
          CuesheetColumnActions.updateItemColumnWidth,
        );
      }
    } else if (cellType === EditableCellTypes.ItemTitle) {
      if (displayType !== DisplayTypes.show) {
        this._changeUtilityColumnWidth(
          type,
          'itemTitleColumn',
          CuesheetColumnActions.updateItemTitleColumnWidth,
        );
      } else {
        this._changeUtilityColumnWidth(
          type,
          'itemColumn',
          CuesheetColumnActions.updateItemColumnWidth,
        );
      }
    } else if (cellType === EditableCellTypes.ItemNote) {
      this._changeNoteColumnWidth(type);
    } else if (cellType === EditableCellTypes.Data) {
      this._changeDataColumnWidth(type);
    }
  },

  _changeUtilityColumnWidth(type, field, action) {
    let currentWidth =
      this.getInState(['settings', field, 'width']) || 'normal';
    let nextWidthToken = this._getNextColumnSize(currentWidth, type);

    if (nextWidthToken) {
      action(nextWidthToken);
    }
  },

  /**
   * Change the width of a data column
   *
   * @param type (shrink|grow)
   * @private
   */
  _changeDataColumnWidth(type) {
    let columnId = this.getSelectedColumnId();
    let curColumnWidth = this.getInState(['columns', columnId, 'width']);

    let nextWidthToken = this._getNextColumnSize(curColumnWidth, type);

    if (nextWidthToken) {
      CuesheetColumnActions.updateColumnWidth(columnId, nextWidthToken);
    }
  },

  /**
   * Change the width of a note column
   *
   * @param type (shrink|grow)
   * @private
   */
  _changeNoteColumnWidth(type) {
    let curColumnWidth = this.getInState([
      'userSettings',
      'noteColumn',
      'width',
    ]);

    let nextWidthToken = this._getNextColumnSize(curColumnWidth, type);

    if (nextWidthToken) {
      CuesheetColumnActions.updateNoteColumnWidth(nextWidthToken);
    }
  },

  /**
   * Given a current size token and show/shrink type, return the next size token
   *
   * @param currentWidthToken
   * @param type (shrink|grow)
   * @returns {*}
   * @private
   */
  _getNextColumnSize(currentWidthToken, type) {
    let indx = ColumnWidthTokens.indexOf(currentWidthToken);

    if (type === 'shrink') {
      indx -= 1;
      if (indx < 0) return;
    } else {
      indx += 1;
      if (indx === ColumnWidthTokens.length) return;
    }

    return ColumnWidthTokens[indx];
  },

  /**
   * Show a column
   */
  onShowColumn(columnId) {
    this._showColumnTransient(columnId);
    this._showColumnPersistent(columnId);
  },

  /**
   * Hide a column
   */
  onHideColumn(columnId) {
    // When column is hidden select next cell of next column
    let colId = columnId ? columnId : this.getSelectedColumnId();
    if (!colId) return;

    var next = this.getState(),
      existingOrder = next.get('columnOrder'),
      existingIndex = existingOrder.indexOf(colId);

    /**
     * Prevent user from hiding last data column.
     * If the user hides the their last data column, they will not be able to
     * re-show all they're columns. The only place that you can select "Show All
     * Columns" is in a data cell column menu. This is why we prevent it.
     * The columns will forever be lost in a black hole... jk.
     */
    if (existingIndex === 0 && existingOrder.size < 2) return;

    let nextColumnId = existingOrder.get(existingIndex + 1);
    let rowIndex = this.getLastSelectedCellRowIndex();

    if (!readOnlyHelper.isShowReadOnly) {
      CuesheetCellActions.selectDataCell(rowIndex, nextColumnId);
    }

    this.hideColumnTransient(colId);
    this._hideColumnPersistent(colId);
  },

  /**
   * Show all columns for a user
   */
  onShowColumns() {
    this._showColumnsPersistent();
  },

  /**
   * Show the new columns when we get the list from the server.
   *
   * NOTE: In most cases we do an optimistic update, and nothing when the async operation completes. In this case we don't do an optimistic
   * update, but rather wait for the response, which contains the order and the list of columns. We do this so we don't have
   * to maintain a separate list in the store that contains the entire list of columns (hidden or otherwise)
   */
  onShowColumnsPersistentCompleted(result) {
    CuesheetRowActions.bustRowHeightCache();

    let next = this.setColumnState(List(result.order), result.columns);

    next = this.recalculateCellSelectionRange(next);

    this.setStateAndTrigger(next);
  },

  /**
   * Delete a column
   */
  onDeleteColumn(columnId) {
    const state = this.getState();

    /*
     * When show contains global element rows, alert the user that deleting a
     * column could cause a discrepancy between current show columns and global
     * element manager element set columns.
     * See: https://github.com/SHOFLO/Issues/issues/4099
     */
    if (
      state.get('globalElementShowItems') &&
      state.get('globalElementShowItems').length > 0
    ) {
      AlertActions.show(
        MessageConstants.globalElementColumnDiscrepancyWarning,
        UIRegions.Cuesheet,
      );
    }

    this.deleteColumnTransient(columnId);
    this._deleteColumnPersistent(columnId);
  },

  deleteColumnTransient(columnId) {
    CuesheetRowActions.bustRowHeightCache();

    let nextState = this.getState().withMutations((st) => {
      st.update('fullColumnOrder', (order) =>
        order.delete(order.indexOf(columnId)),
      );
      st.update('fullColumns', (columns) => columns.delete(columnId));
      st.update('columnOrderGuest', (order) =>
        order.delete(order.indexOf(columnId)),
      );
      st.update('columnsGuestPass', (columns) => columns.delete(columnId));
      st.update('columnOrder', (order) =>
        order.delete(order.indexOf(columnId)),
      );
      st.update('columns', (columns) => columns.delete(columnId));
      st.set(
        'selectableCells',
        this.getSelectableCellList(st.get('columnOrder')),
      );
    });

    nextState = this.removeCellSelectionsByColumnIds(columnId, nextState);
    nextState = this.recalculateCellSelectionRange(nextState);

    this.setStateAndTrigger(nextState);
  },

  _deleteColumnPersistent(columnId) {
    let state = this.getState();

    CuesheetColumnActions.deleteColumnPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      columnId,
    );
  },

  /**
   * Delete a guest pass column
   */
  onDeleteColumnGuestPass(columnId) {
    this._deleteColumnGuestPassPersistent(columnId);
  },

  deleteColumnGuestPassTransient(columnId) {
    this.setStateAndTrigger(
      this.getState().update('columnsGuestPass', (columns) =>
        columns.delete(columnId),
      ),
    );
  },

  onDeleteColumnGuestPassPersistentCompleted(itemSetList) {
    this._setGuestPassStateFromResponse(itemSetList);
  },

  /**
   * Take the guest column order and the shared columns from a guest pass
   * response
   */
  _setGuestPassStateFromResponse(itemSetList) {
    let nextState = this.getState().set(
      'columnOrderGuest',
      CuesheetDataHelper.getGuestColumnOrder(itemSetList),
    );

    nextState = this.setGuestPassColumnState(
      itemSetList.columnOrders.guestPass,
      itemSetList.columns,
      nextState,
    );

    this.setStateAndTrigger(nextState);
  },

  _deleteColumnGuestPassPersistent(columnId) {
    let state = this.getState();

    CuesheetColumnActions.deleteColumnGuestPassPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      columnId,
    );
  },

  /**
   * Add a new column
   */
  onAddColumn() {
    const state = this.getState();

    /*
     * When show contains global element rows, alert the user that adding  a
     * column could cause a discrepancy between current show columns and global
     * element manager element set columns.
     * See: https://github.com/SHOFLO/Issues/issues/4099
     */
    if (
      state.get('globalElementShowItems') &&
      state.get('globalElementShowItems').length > 0
    ) {
      AlertActions.show(
        MessageConstants.globalElementColumnDiscrepancyWarning,
        UIRegions.Cuesheet,
      );
    }

    /**
     * We clear the scroll index state when a column is added (ref #1290)
     */
    this.setStateAndTrigger(this.getClearedScrollIndexState());

    this._addColumnPersistent();
  },

  _addColumnPersistent() {
    let state = this.getState();

    CuesheetColumnActions.addColumnPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      { title: 'Untitled' },
    );
  },

  /**
   * NOTE: for adding columns, we don't do optimistic update, since we're waiting for the column ID.
   */
  onAddColumnPersistentCompleted(result) {
    this.addColumnTransient(result.column);
  },

  /**
   * Adds an existing column to the guest pass column order
   */
  onAddColumnGuestPass(columnId) {
    let nextState = this.getState();
    let columnOrderGuest = nextState.get('columnOrderGuest');
    columnOrderGuest = columnOrderGuest.concat(columnId);

    nextState = nextState.set('columnOrderGuest', columnOrderGuest);

    this.setStateAndTrigger(nextState);

    CuesheetColumnActions.addColumnGuestPassPersistent(
      nextState.get('teamId'),
      nextState.get('eventId'),
      nextState.get('showId'),
      nextState.get('itemSetListId'),
      columnId,
    );
  },

  onAddColumnGuestPassPersistentCompleted(itemSetList) {
    // The API puts a column that's shared again back in its old slot, not
    // at the end where onAddColumnGuestPass put it, so take the order from
    // the response. The guest column arrows find neighbors in this order.
    this._setGuestPassStateFromResponse(itemSetList);
  },

  /**
   * Move a column right for a user
   */
  onMoveUserColumnRight(columnId) {
    let id = columnId || this.getSelectedColumnId();
    if (!id) return;

    this._moveUserColumnRight(id);
  },

  /**
   * Move a column right for guests
   */
  onMoveGuestColumnRight(columnId) {
    let id = columnId;
    if (!id) return;

    this._moveGuestColumnRight(id);
  },

  /**
   * Move a column left for a user
   *
   * NOTE: moving a column left is just moving the one to the left of it right
   */
  onMoveUserColumnLeft(columnId) {
    let columnIdToMove = this._getColumnToTheLeft(columnId);

    if (columnIdToMove) this._moveUserColumnRight(columnIdToMove);
  },

  /**
   * Move a column left for guests

   * NOTE: moving a column left is just moving the one to the left of it right
   */
  onMoveGuestColumnLeft(columnId) {
    let columnIdToMove = this._getColumnToTheLeft(columnId, 'guest');

    if (columnIdToMove) this._moveGuestColumnRight(columnIdToMove);
  },

  _getColumnToTheLeft(columnId, type) {
    let orderType = !type ? 'columnOrder' : 'columnOrderGuest';

    let id = columnId || this.getSelectedColumnId();
    if (!id) return;

    var next = this.getState(),
      existingOrder = next.get(orderType),
      existingIndex = existingOrder.indexOf(id);

    if (existingIndex === 0) return;

    return existingOrder.get(existingIndex - 1);
  },

  onMoveColumnRight(columnId) {
    let id = columnId || this.getSelectedColumnId();
    if (!id) return;

    this._moveColumnRight(id);
  },

  onMoveColumnLeft(columnId) {
    let columnIdToMove = this._getColumnToTheLeft(columnId);

    if (columnIdToMove) this._moveColumnRight(columnIdToMove);
  },

  onRestoreColumn(columnId) {
    let state = this.getState();

    CuesheetColumnActions.restoreColumnPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      columnId,
    );
  },

  onRestoreColumnPersistentCompleted(result) {
    this.restoreColumn(result);
  },

  restoreColumn(result) {
    this.addColumnTransient(result.column);
  },

  updateItemColumnSettingsTransient(changes, fieldName = 'itemColumn') {
    let state = this.getState().mergeIn(['settings', fieldName], Map(changes));

    if (typeof changes.displayDescriptions !== 'undefined') {
      state = this.touchAllRows(state);
    }

    CuesheetRowActions.bustRowHeightCache();

    this.setStateAndTrigger(state);
  },

  /**
   * Add a column to UI state
   */
  addColumnTransient(column) {
    //check if we need to add column back go guest column order

    this.setStateAndTrigger(
      this.getState().withMutations((st) => {
        st.update('fullColumnOrder', (order) => order.push(column._id));
        st.update('fullColumns', (columns) =>
          columns.set(column._id, Map(column)),
        );
        st.update('columnOrder', (order) => order.push(column._id));
        st.update('columns', (columns) => columns.set(column._id, Map(column)));
        // eslint-disable-next-line no-unused-expressions
        st.set(
          'selectableCells',
          this.getSelectableCellList(st.get('columnOrder')),
        ),
          st.update('columnsGuestPass', (columns) =>
            columns.set(column._id, Map(column)),
          );
        st.update('columnOrderGuest', (order) => order.push(column._id));
      }),
    );
  },

  _showColumnsPersistent() {
    let state = this.getState();

    CuesheetColumnActions.showColumnsPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      state.get('userId'),
    );
  },

  _hideColumnPersistent(columnId) {
    let state = this.getState();

    CuesheetColumnActions.hideColumnPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      state.get('userId'),
      columnId,
    );
  },

  _updateColumnWidthTransient(columnId, width) {
    let next = this.getState();

    CuesheetRowActions.bustRowHeightCache();

    this.setStateAndTrigger(next.setIn(['columns', columnId, 'width'], width));
  },

  _updateColumnWidthPersistent(columnId, width) {
    let state = this.getState();

    CuesheetColumnActions.updateColumnWidthPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      columnId,
      width,
    );
  },

  _showColumnTransient(columnId) {
    //TODO: this currently is only used by the 'responsive' view..at some point we need to make it work w/ all views, but leaving as debt for now..

    //1. Get the index in the full column order
    let fullColumnIndex = this.getInState('fullColumnOrder').indexOf(columnId);
    //2. Splice the value back into the columnOrder
    let next = this.getState().update('columnOrder', (order) =>
      order.splice(fullColumnIndex, 0, columnId),
    );

    this.setStateAndTrigger(next);
  },

  _showColumnPersistent(columnId) {
    let state = this.getState();

    CuesheetColumnActions.showColumnPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      state.get('userId'),
      columnId,
    );
  },

  hideColumnTransient(columnId) {
    CuesheetRowActions.bustRowHeightCache();

    this.setStateAndTrigger(
      this.getState().withMutations((st) => {
        st.update('columnOrder', (order) =>
          order.delete(order.indexOf(columnId)),
        );
        st.update('columns', (columns) => columns.delete(columnId));
        st.set(
          'selectableCells',
          this.getSelectableCellList(st.get('columnOrder')),
        );
      }),
    );
  },

  _moveUserColumnPersistent(columnId, afterColumnId) {
    let state = this.getState();

    CuesheetColumnActions.moveUserColumnPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      state.get('userId'),
      columnId,
      afterColumnId,
    );
  },

  _moveGuestColumnPersistent(columnId, afterColumnId) {
    let state = this.getState();

    CuesheetColumnActions.moveGuestColumnPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      state.get('userId'),
      columnId,
      afterColumnId,
    );
  },

  _moveColumnPersistent(columnId, afterColumnId) {
    let state = this.getState();

    CuesheetColumnActions.moveColumnPersistent(
      state.get('teamId'),
      state.get('eventId'),
      state.get('showId'),
      state.get('itemSetListId'),
      columnId,
      afterColumnId,
    );
  },

  _moveUserColumnRight(columnId) {
    let afterColumnId = this.moveColumnTransient(columnId);

    if (afterColumnId) this._moveUserColumnPersistent(columnId, afterColumnId);
  },

  _moveGuestColumnRight(columnId) {
    let afterColumnId = this.moveColumnTransient(
      columnId,
      null,
      'columnOrderGuest',
    );

    if (afterColumnId) this._moveGuestColumnPersistent(columnId, afterColumnId);
  },

  /**
   * Move a column...
   *
   * @param columnId the column to move
   * @param afterColumnId optional place to move the column after...if not present, we assume we're just moving right, and get the next position over
   */
  moveColumnTransient(columnId, afterColumnId, type) {
    let orderType = !type ? 'columnOrder' : 'columnOrderGuest';
    let columnType = !type ? 'columns' : 'columnsGuestPass';

    var next = this.getState(),
      existingOrder = next.get(orderType),
      existingIndex = existingOrder.indexOf(columnId);

    /**
     * This means the column doesn't exist for the local user (in realtime scenarios, a column can be hidden for a user, so we ignore it)
     */
    if (existingIndex === -1 || !existingOrder.indexOf(afterColumnId) === -1)
      return;

    if (existingIndex === existingOrder.size - 1) return;

    if (!afterColumnId) afterColumnId = existingOrder.get(existingIndex + 1);

    next = next.withMutations((st) => {
      st.update(orderType, (order) => {
        /**
         * First remove the existing column
         */
        var nextOrder = order.splice(existingIndex, 1);

        /**
         * Next get the index of the column we're moving the other column after
         */
        let afterColumnExistingIndex = nextOrder.indexOf(afterColumnId);

        /**
         * Finally add the column back into the order with the new position
         */
        return nextOrder.splice(afterColumnExistingIndex + 1, 0, columnId);
      });

      st.update(columnType, (columns) =>
        CuesheetDataHelper.rebuildSortedColumns(columns, st.get(orderType)),
      );

      if (type) return;

      st.set('selectableCells', this.getSelectableCellList(st.get(orderType)));
    });

    next = this.recalculateCellSelectionRange(next);

    this.setStateAndTrigger(next);

    return afterColumnId;
  },

  _moveColumnRight(columnId) {
    let afterColumnId = this.moveColumnTransient(columnId);

    if (afterColumnId) this._moveColumnPersistent(columnId, afterColumnId);
  },

  /**
   * Remove/delete a field for all columns (if necessary)
   *
   * @param fieldName
   * @param state
   * @returns {*}
   */
  clearColumnFieldState(fieldName, state) {
    //Don't mutate the columns unless necessary..
    if (
      state.get('columns') &&
      state.get('columns').some((col) => col.get(fieldName))
    ) {
      state = state.update('columns', (cols) =>
        cols.map((col) => col.delete(fieldName)),
      );
    }

    return state;
  },
};
